import { DateTime } from 'luxon';
import {
  CashPaymentMethod,
  CheckedPaymentResponse,
  CheckPaymentMethod,
  InvoicePayment,
  NonePaymentMethod,
  OrderPayment,
  OrderPaymentStatus,
  OrderPaymentType,
  Payment,
  PaymentDetail,
  PaymentMethod,
  PaymentMethodType,
  PaymentReason,
  PaymentStatus,
  StripeCard,
  StripeCardType,
} from './payment';
import { OnAccountParsers } from './on-account/on-account-parsers';
import Dinero from 'dinero.js';
import { StripeBankAccount } from './payment-v2';

export namespace PaymentParsers {
  import OnAccountJson = OnAccountParsers.OnAccountJson;

  export class OrderPaymentJson {
    id!: number;
    orderId!: number;
    paymentMethod!: string;
    gatewayTransactionId!: string;
    amount!: number;
    balance!: number;
    gatewayStatus!: string;
    comments!: string;
    gatewayResponse!: string;
    paymentType!: OrderPaymentType;
    date!: string;
    status!: PaymentStatus;
    reason!: PaymentReason;
    method!: PaymentMethodType;
    methodDetail?: string;
    refunded?: boolean;
    refundedPaymentId?: number;
    refunds!: OrderPaymentJson[];
    addedBy?: number;
    taxCharged?: number;
  }

  export function toOrderPayment(input: OrderPaymentJson): OrderPayment {
    return {
      id: input.id,
      orderId: input.orderId,
      paymentMethod: input.paymentMethod,
      gatewayTransactionId: input.gatewayTransactionId,
      amount: input.amount,
      balance: input.balance,
      gatewayStatus: input.gatewayStatus,
      comments: input.comments,
      gatewayResponse: input.gatewayResponse,
      paymentType: input.paymentType,
      date: DateTime.fromISO(input.date, { setZone: true }),
      status: input.status,
      reason: input.reason,
      method: input.method,
      methodDetail: input.methodDetail,
      refunded: input.refunded,
      refundedPaymentId: input.refundedPaymentId,
      refunds: input.refunds.map(toOrderPayment),
      addedBy: input.addedBy,
      taxCharged: input.taxCharged,
    };
  }

  export class OrderPaymentStatusJson {
    status!: boolean;
    message?: string;
    payment?: OrderPaymentJson;
  }

  export function toOrderPaymentStatus(input: OrderPaymentStatusJson): OrderPaymentStatus {
    return {
      status: input.status,
      message: input.message,
      payment: input.payment ? toOrderPayment(input.payment) : undefined,
    };
  }

  export type StripeCardJson = {
    id: number;
    userId: number;
    stripeCardId: string;
    cardholderName: string;
    brand: string;
    expirationMonth: string;
    expirationYear: string;
    last4: string;
    nickname?: string;
    cardType?: StripeCardType;
    zip?: string;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
    lockTimestamp?: string;
    setupIntentId?: string;
    confirmationTokenId?: string;
  };

  export function toStripeCard(input: StripeCardJson): StripeCard {
    return {
      id: input.id,
      userId: input.userId,
      stripeCardId: input.stripeCardId,
      cardholderName: input.cardholderName,
      brand: input.brand,
      expirationMonth: input.expirationMonth,
      expirationYear: input.expirationYear,
      last4: input.last4,
      nickname: input.nickname,
      cardType: input.cardType,
      zip: input.zip,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      updatedAt: DateTime.fromISO(input.updatedAt, { setZone: true }),
      deletedAt: input.deletedAt ? DateTime.fromISO(input.deletedAt, { setZone: true }) : undefined,
      lockTimestamp: input.lockTimestamp ? DateTime.fromISO(input.lockTimestamp, { setZone: true }) : undefined,
      setupIntentId: input.setupIntentId,
      confirmationTokenId: input.confirmationTokenId,
    };
  }

  export type StripePaymentMethodJson = {
    type: PaymentMethodType.Stripe;
    card?: StripeCardJson;
    method?: string;
    bankAccount?: StripeBankAccount;
  };

  export type OnAccountPaymentMethodJson = {
    type: PaymentMethodType.OnAccount;
    account: OnAccountJson;
  };

  export type PaymentMethodJson =
    | StripePaymentMethodJson
    | CashPaymentMethod
    | CheckPaymentMethod
    | OnAccountPaymentMethodJson
    | NonePaymentMethod;

  export function toPaymentMethod(input: PaymentMethodJson): PaymentMethod {
    switch (input.type) {
      case PaymentMethodType.Stripe:
        return {
          type: input.type,
          method: input.method,
          card: input.card ? toStripeCard(input.card) : undefined,
          bankAccount: input.bankAccount,
        };
      case PaymentMethodType.OnAccount:
        return {
          type: input.type,
          account: OnAccountParsers.toOnAccount(input.account),
        };
      default:
        return input;
    }
  }

  export type PaymentJson = {
    id: string;
    invoiceId: string;
    method: string;
    methodDetail?: string;
    status: PaymentStatus;
    gatewayId?: string;
    gatewayStatus?: string;
    gatewayResponse?: string;
    amount: number;
    refundedPaymentId?: string;
    userId: number;
    invoicePayments: InvoicePaymentJson[];
    paidAt: string;
    createdBy?: number;
    processingFee?: number;
    availableAmount: number;
  };

  export type CheckedPaymentJson = {
    pending: PaymentJson[];
    suspicious: PaymentJson[];
    corrected: PaymentJson[];
  };

  export interface InvoicePaymentJson {
    id: string;
    invoiceId: string;
    paymentId: string;
    amount: number;
    updatedAt?: string;
    createdAt?: string;
    deletedAt?: string;
  }

  export function toInvoicePayment(input: InvoicePaymentJson): InvoicePayment {
    return {
      id: input.id,
      invoiceId: input.invoiceId,
      paymentId: input.paymentId,
      amount: input.amount,
      updatedAt: input.updatedAt ? DateTime.fromISO(input.updatedAt, { setZone: true }) : undefined,
      createdAt: input.createdAt ? DateTime.fromISO(input.createdAt, { setZone: true }) : undefined,
      deletedAt: input.deletedAt ? DateTime.fromISO(input.deletedAt, { setZone: true }) : undefined,
    };
  }

  export function toPayment(input: PaymentJson): Payment {
    return {
      id: input.id,
      invoiceId: input.invoiceId,
      method: input.method,
      methodDetail: input.methodDetail,
      status: input.status,
      gatewayId: input.gatewayId,
      gatewayStatus: input.gatewayStatus,
      gatewayResponse: input.gatewayResponse,
      amount: input.amount,
      refundedPaymentId: input.refundedPaymentId,
      userId: input.userId,
      invoicePayments: input.invoicePayments.map(toInvoicePayment),
      paidAt: DateTime.fromISO(input.paidAt, { setZone: true }),
      createdBy: input.createdBy,
      processingFee: input.processingFee,
      availableAmount: input.availableAmount,
    };
  }

  export function toCheckedPaymentResponse(input: CheckedPaymentJson): CheckedPaymentResponse {
    return {
      pending: input.pending.map(toPayment),
      suspicious: input.suspicious.map(toPayment),
      corrected: input.corrected.map(toPayment),
    };
  }

  export type PaymentDetailJson = {
    intentId: string;
    type: 'CardPresent' | 'CardNotPresent' | 'Electronic';
    amount: Dinero.Options;
  };

  export function toPaymentDetail(input: PaymentDetailJson): PaymentDetail {
    return { ...input, amount: Dinero(input.amount) };
  }
}
